/**
 * Security Policy Card Component
 * Displays security policy settings with icons
 */

import React from 'react';
import { Shield, Key, Clock } from 'lucide-react';
import { SecurityPolicies } from '../utils/tokenManager';

interface SecurityPolicyCardProps {
  policies: SecurityPolicies;
  editable?: boolean;
  onEdit?: () => void;
}

const SecurityPolicyCard: React.FC<SecurityPolicyCardProps> = ({
  policies,
  editable = false,
  onEdit,
}) => {
  const policyItems = [
    {
      icon: Shield,
      label: 'Multi-Factor Authentication',
      value: 'Coming Soon',
      enabled: false,
      comingSoon: true,
    },
    {
      icon: Clock,
      label: 'Password Expiration',
      value: policies.passwordExpirationDays
        ? `Every ${policies.passwordExpirationDays} days`
        : 'Never expires',
      enabled: !!policies.passwordExpirationDays,
      comingSoon: false,
    },
    {
      icon: Key,
      label: 'API Key Access',
      value: policies.enableApiKeys ? 'Enabled' : 'Disabled',
      enabled: policies.enableApiKeys,
      comingSoon: false,
    },
  ];

  return (
    <div
      className="backdrop-blur-xl rounded-xl p-6 border"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', borderColor: 'rgba(255, 255, 255, 0.1)' }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Security Policies</h3>
        {editable && onEdit && (
          <button
            onClick={onEdit}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
            style={{
              background: 'linear-gradient(135deg, #AD0000 0%, #9A1220 100%)',
              color: 'white',
            }}
          >
            Edit Policies
          </button>
        )}
      </div>

      <div className="space-y-3">
        {policyItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <div
              key={index}
              className={`flex items-center justify-between p-3 rounded-lg ${item.comingSoon ? 'opacity-60' : ''}`}
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
            >
              <div className="flex items-center space-x-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{
                    backgroundColor: item.comingSoon
                      ? 'rgba(59, 130, 246, 0.2)'
                      : item.enabled
                      ? 'rgba(34, 197, 94, 0.2)'
                      : 'rgba(107, 114, 128, 0.2)',
                  }}
                >
                  <Icon
                    className="w-5 h-5"
                    style={{
                      color: item.comingSoon ? '#60a5fa' : item.enabled ? '#22c55e' : '#9ca3af',
                    }}
                  />
                </div>
                <div>
                  <p className={`text-sm font-medium ${item.comingSoon ? 'text-gray-400' : 'text-white'}`}>
                    {item.label}
                  </p>
                  <p className="text-xs text-white/60">{item.value}</p>
                </div>
              </div>
              {item.comingSoon ? (
                <div className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                  Coming Soon
                </div>
              ) : (
                <div
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    item.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                  }`}
                >
                  {item.enabled ? 'Active' : 'Inactive'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SecurityPolicyCard;
